import React, { useState } from 'react';
import { HorizontalDivider } from 'components/HorizontalDivider/HorizontalDivider';
import InputText from 'components/InputText/InputText';
import { Instruction } from 'components/Instruction/Instruction';
import { Link } from 'react-router-dom';
import { routes } from 'routing/routes';
import referAFriend from '../../assets/img/egg/refer-a-friend.png';
import referAFriendMobile from '../../assets/img/egg/refer-a-friend-mobile.png';
import { Chain } from 'components/Icons/Icons';

import './Referral.scss';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useUserReferral } from 'contexts/UserReferralContext';
import Spinner from 'components/Spinner';
import { DropdownAnchor } from 'components/Dropdown/DropdownAnchor';
import { ReferralSharing } from 'components/Sharing/ShareTargets';
import { useLoginRedirect } from 'hooks/useLoginRedirect';
import { Egg } from './Egg';

const COPY_LINK: string = 'Copy Link';
const COPIED_LINK: string = 'Link Copied';
const REFERRAL_OMNIGROW_AWARD = 2500;

export const Referral = () => {
  const [buttonCopy, setButtonCopy] = useState(COPY_LINK);
  const { rewards, referralCode } = useUserReferral();
  const referralLink = referralCode.data;
  const { pointsAwarded, mintSpotAwarded } = rewards.data ?? {
    pointsAwarded: 0,
    mintSpotAwarded: 0,
  };

  const copyReferralLink = (link: string) => {
    navigator.clipboard.writeText(link);
    setButtonCopy(COPIED_LINK);
    setTimeout(function () {
      setButtonCopy(COPY_LINK);
    }, 3000);
  };
  useLoginRedirect({ follow: routes.referral() });

  const pageContent = (
    <div className="refer-a-friend-container">
      <div className="refer-a-friend-info">
        <div className="refer-a-friend-header">
          <h1>Refer a friend</h1>
          <p>
            Share your unique referral link with your friends to earn a <b>Gribble Egg mint spot</b>
            . You and your friend will also both receive <b>{REFERRAL_OMNIGROW_AWARD} OmniGrow</b>!
          </p>

          {referralLink ? (
            <div>
              <div className="refer-a-friend-link">
                <InputText
                  placeholder="your referral link"
                  disabled
                  type={'text'}
                  value={referralLink.replace('https://', '')}
                  id={'referal-link'}
                />
                <div className="share-options">
                  <button
                    className="btn btn-primary btn-sm copy-link-button"
                    onClick={() => copyReferralLink(referralLink)}
                  >
                    <Chain /> {buttonCopy}
                  </button>
                  <DropdownAnchor>
                    <ReferralSharing referralLink={new URL(referralLink)} />
                  </DropdownAnchor>
                </div>
              </div>
              <div className="refer-a-friend-rewards">
                <span>
                  Mint spots earned: <b>{mintSpotAwarded}</b>
                  <hr className="vertical-divider" />
                  OmniGrow earned: <b>{pointsAwarded}</b>
                </span>
              </div>
            </div>
          ) : (
            <Spinner color="white" width={20} height={20} />
          )}
        </div>
        <HorizontalDivider />
        <div className="refer-a-friend-steps">
          <h2>How it works?</h2>
          <Instruction
            number={1}
            title="Share your referral link"
            description={<>Copy the link above, and send it to a friend</>}
          />
          <Instruction
            number={2}
            title="Have them sign up and Play To Mint"
            description={
              <>
                Your friend will be directed to register, and will be able to{' '}
                <Link to={routes.playToMint()}> Play To Mint</Link>.
              </>
            }
          />
          <Instruction
            number={3}
            title="Claim your rewards"
            description={
              <>
                You&apos;ll receive an <b>extra mint spot</b>. Both of you will receive{' '}
                <b>{REFERRAL_OMNIGROW_AWARD} extra OmniGrow</b>.<br />A successful referral
                completion may take up to 30 minutes to apply.
              </>
            }
          />
        </div>
      </div>
      <div className="refer-a-friend-image-container">
        <div className="refer-a-friend-image-overlay" />
        <picture className="refer-a-friend-image">
          <source media="(max-width: 767px)" srcSet={referAFriendMobile} />
          <LazyLoadImage src={referAFriend} alt="Refer a friend" />
        </picture>
      </div>
    </div>
  );

  return <Egg>{pageContent}</Egg>;
};
